// filepath: /Users/urfielahi/Documents/CloudArc/Ace-shadCN/src/pages/auth-callback-page.tsx
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { authService } from '@/services/auth-service';
import { handleOAuthCallbackData } from '@/utils/oauth-callback-handler';

/**
 * AuthCallbackPage handles OAuth callbacks (Google, etc.)
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [processingAuth, setProcessingAuth] = useState(true);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent multiple executions
      if (hasProcessedRef.current) {
        return;
      }
      
      hasProcessedRef.current = true;
      
      try {
        setProcessingAuth(true);
        
        // Use the utility function to handle OAuth callback data
        const result = handleOAuthCallbackData(searchParams);
        
        if (result.success) {
          navigate('/dashboard', { replace: true });
          return;
        } else if (result.error) {
          throw new Error(result.error);
        }
        
        // Check if we have an authenticated session (fallback)
        const isAuthenticated = authService.isAuthenticated();
        const session = authService.getStoredSession();
        
        if (isAuthenticated && session?.user) {
          console.log('AuthCallbackPage: User already authenticated, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
          return;
        }
        
        // If we reach here, no valid callback data was found
        throw new Error('No valid authentication data found in callback');
        
      } catch (err: any) {
        console.error('AuthCallbackPage: Authentication error:', err);
        setError(err.message || 'Authentication failed. Please try again.');
        setProcessingAuth(false);
        hasProcessedRef.current = false; // Reset on error so user can retry
      }
    };
    
    handleCallback();
  }, []); // Remove dependencies to prevent re-execution

  return (
    <div className="flex items-center justify-center h-full">
      <Card className="w-[450px] mx-auto">
        <CardContent className="pt-6">
          {processingAuth ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Completing authentication...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="bg-destructive/10 text-destructive p-3 rounded-md mb-4">
                {error}
              </div>
              <Button 
                variant="outline"
                onClick={() => navigate('/login')} 
              >
                Return to login
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="bg-green-100 text-green-800 p-3 rounded-md mb-4">
                Authentication successful! Redirecting...
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
